export const API_BASE =
  ((import.meta.env.VITE_API_BASE as string | undefined) ??
    (import.meta.env.VITE_API_URL as string | undefined) ??
    "")
const CORRELATION_STORAGE_KEY = "salesdemon.correlation_id"

export function resolveCorrelationId(): string | undefined {
  if (typeof window === "undefined") return undefined
  const globalId = (window as typeof window & { __CORRELATION_ID__?: unknown }).__CORRELATION_ID__
  if (typeof globalId === "string" && globalId.trim() !== "") {
    return globalId
  }

  try {
    const stored = window.sessionStorage.getItem(CORRELATION_STORAGE_KEY)
    if (stored && stored.trim() !== "") {
      return stored
    }
    if (typeof window.crypto?.randomUUID === "function") {
      const generated = window.crypto.randomUUID()
      window.sessionStorage.setItem(CORRELATION_STORAGE_KEY, generated)
      return generated
    }
  } catch (error) {
    console.warn("Failed to resolve correlation id", error)
  }
  return undefined
}

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown
  readonly correlationId?: string

  constructor(message: string, status: number, body: unknown, correlationId?: string) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.body = body
    this.correlationId = correlationId
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set("Accept", "application/json")
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData
  if (!headers.has("Content-Type") && init.body && !isFormData) {
    headers.set("Content-Type", "application/json")
  }

  const correlationId = resolveCorrelationId()
  if (correlationId && !headers.has("X-Correlation-ID")) {
    headers.set("X-Correlation-ID", correlationId)
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: init.credentials ?? "include",
  })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const body = text ? safeJsonParse(text) : undefined

  if (!response.ok) {
    const detail =
      body && typeof body === "object" && "detail" in (body as Record<string, unknown>)
        ? String((body as Record<string, unknown>).detail)
        : undefined
    const correlationId = response.headers.get("x-correlation-id") ?? undefined
    throw new ApiError(detail ? `API ${response.status}: ${detail}` : `API ${response.status}`, response.status, body, correlationId)
  }

  return body as T
}

function safeJsonParse(payload: string): unknown {
  try {
    return JSON.parse(payload)
  } catch {
    return payload
  }
}
